import {
  Box,
  Heading,
  IconButton,
  Link,
  Separator,
  Table,
  Text,
  Timeline,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { FaCheckCircle, FaLink, FaPhoneAlt, FaRocket } from 'react-icons/fa';
import type { LearnMoreSection } from '../LearnMore';
import useLearnMoreSections from '../hooks/useLearnMoreSections';

const SECTIONS: LearnMoreSection[] = [
  { id: 'cost-of-olympiq', label: 'Cost of Olympiq' },
  { id: 'payments-explained', label: 'Payments explained' },
  { id: 'price-example', label: 'Pricing example' },
  { id: 'getting-setup', label: 'Getting setup' },
];

const TIMELINE_ICON_SIZE = 12;

const GETTING_SETUP_STEPS = [
  {
    icon: <FaPhoneAlt size={TIMELINE_ICON_SIZE} />,
    title: '1. Contact us',
    body: `The first step is to contact us. From there we can explain the process and answer any questions you have.`,
  },
  {
    icon: <FaLink size={TIMELINE_ICON_SIZE} />,
    title: '2. Stripe onboarding link',
    body: `We will then send over a secure Stripe onboarding link (you can use this link to connect to an existing Stripe account if you already have one). The form takes roughly 20 minutes to complete, and you'll need to hand your ID and club bank account details where funds will be paid into. You'll need to specify what type of buisness you are - for most sports clubs that's a unincorporated association.`,
  },
  {
    icon: <FaCheckCircle size={TIMELINE_ICON_SIZE} />,
    title: '3. Let us know once complete',
    body: `Once Stripe is connected, drop us a note so we can run a test transaction, and begin the process of setting up your website and mobile app.`,
  },
  {
    icon: <FaRocket size={TIMELINE_ICON_SIZE} />,
    title: '4. Ready to take payments!',
    body: `You're ready to take payments! And can get paid the very next day!`,
  },
];

const SeamlessPaymentsRefunds = (): React.JSX.Element => {
  useLearnMoreSections(SECTIONS, { pageTitle: 'Seamless Payments & Refunds' });

  return (
    <VStack align="stretch" gap={12}>
      <Box>
        <Heading size="3xl" letterSpacing="-0.04em">
          Seamless payments & refunds
        </Heading>
        <Text marginTop={4} color="gray.600" fontSize="lg" maxW="3xl" _dark={{ color: 'gray.300' }}>
          Take payments, issue refunds, and keep statement descriptors showing your club name.
          Stripe powers the payments behind the scenes, and Olympiq links each payment to the right
          member and booking so your records stay up to date.
        </Text>
      </Box>

      <Separator />

      <SectionBlock id="cost-of-olympiq" title="Cost of Olympiq">
        <Text>
          As a club, you never directly pay for Olympiq - it is free to use. However we do charge a
          small application fee on every payment processed (membership renewals, sign-ups, and
          session bookings). The fee is deducted from the payment, so there are no invoices to
          manage. It is 10p per paid session booking (no charge for free sessions), £4 for annual
          membership payments, and 33p for monthly membership payments. Stripe also charges a small
          processing fee, outlined in the next
          section.
        </Text>
      </SectionBlock>

      <SectionBlock id="payments-explained" title="Payments explained">
        <Text>
          We process every membership fee, session booking, and refund through{' '}
          <Link href="https://stripe.com" color="blue.500" target="_blank" rel="noreferrer">
            Stripe
          </Link>
          , an enterprise payment provider. Your club pays the standard{' '}
          <Link href="https://stripe.com/pricing" color="blue.500" target="_blank" rel="noreferrer">
            Stripe fees
          </Link>{' '}
          plus the Olympiq application fee listed in the{' '}
          <Link href="/#pricing" color="blue.500">
            Pricing section
          </Link>{' '}
          of the home page - no hidden extras.
        </Text>
        <Text>
          When a member pays, their bank statement shows your club name rather than Olympiq, so
          there’s no confusion about who they’ve paid.
        </Text>
        <Text>
          You retain full ownership of the Stripe account. Olympiq simply initiates{' '}
          <Link
            href="https://stripe.com/docs/connect/direct-charges"
            color="blue.500"
            target="_blank"
            rel="noreferrer"
          >
            direct charges
          </Link>{' '}
          on your behalf, meaning funds flow straight to your Stripe balance and you can manage
          payouts, refunds, and reporting exactly as you want to.
        </Text>
        <Text>
          Card data stays secure inside Stripe, and Apple Pay plus Google Pay are enabled by
          default. You can even switch on alternative payment methods like Klarna or Link, making it
          effortless for members to check out quickly on any device.
        </Text>
      </SectionBlock>

      <SectionBlock id="price-example" title="Pricing example">
        <Text>
          Here are three worked examples: a session booking of £6.00, an annual membership payment
          of £40.00, and a monthly membership payment of £40.00. These assume Stripe charges 1.5% +
          10p, plus the Olympiq application fees listed above.
        </Text>

        <Box overflowX="auto" width="full">
          <Table.Root size="sm" variant="outline" minWidth="720px">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Payment</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">Amount</Table.ColumnHeader>
                <Table.ColumnHeader>Stripe fee</Table.ColumnHeader>
                <Table.ColumnHeader>Olympiq fee</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">Total fees</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">Net to club</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.Cell fontWeight="semibold">Session booking</Table.Cell>
                <Table.Cell textAlign="right">£6.00</Table.Cell>
                <Table.Cell>19p (1.5% + 10p)</Table.Cell>
                <Table.Cell>10p</Table.Cell>
                <Table.Cell textAlign="right">29p</Table.Cell>
                <Table.Cell textAlign="right">£5.71</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell fontWeight="semibold">Annual membership payment</Table.Cell>
                <Table.Cell textAlign="right">£40.00</Table.Cell>
                <Table.Cell>70p (1.5% + 10p)</Table.Cell>
                <Table.Cell>£4.00</Table.Cell>
                <Table.Cell textAlign="right">£4.70</Table.Cell>
                <Table.Cell textAlign="right">£35.30</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell fontWeight="semibold">Monthly membership payment</Table.Cell>
                <Table.Cell textAlign="right">£40.00</Table.Cell>
                <Table.Cell>70p (1.5% + 10p)</Table.Cell>
                <Table.Cell>33p</Table.Cell>
                <Table.Cell textAlign="right">£1.03</Table.Cell>
                <Table.Cell textAlign="right">£38.97</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table.Root>
        </Box>
      </SectionBlock>

      <SectionBlock id="getting-setup" title="Getting setup">
        <Text>
          Getting started only takes a few steps. We stay with you through onboarding so payments
          and refunds go live without downtime.
        </Text>
        <Timeline.Root colorPalette="blue" size="lg" marginTop={6}>
          {GETTING_SETUP_STEPS.map((step) => (
            <Timeline.Item key={step.title}>
              <Timeline.Connector>
                <Timeline.Separator />
                <Timeline.Indicator bg="gray.800" color="white">
                  {step.icon}
                </Timeline.Indicator>
              </Timeline.Connector>
              <Timeline.Content>
                <Timeline.Title fontSize="md" fontWeight="semibold">
                  {step.title}
                </Timeline.Title>
                <Timeline.Description>
                  <Text color="gray.600" whiteSpace="pre-line" _dark={{ color: 'gray.300' }}>
                    {step.body}
                  </Text>
                </Timeline.Description>
              </Timeline.Content>
            </Timeline.Item>
          ))}
        </Timeline.Root>
      </SectionBlock>
    </VStack>
  );
};

interface SectionBlockProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

const SectionBlock = ({ id, title, children }: SectionBlockProps): React.JSX.Element => (
  <Box as="section" id={id} scrollMarginTop="120px">
    <Heading size="2xl" fontWeight="bold" marginBottom={3}>
      {title}
      <IconButton
        aria-label="Copy link"
        variant="ghost"
        size="xs"
        onClick={() => {
          navigator.clipboard.writeText(window.location.href);
        }}
      >
        <FaLink />
      </IconButton>
    </Heading>
    <VStack align="stretch" gap={3}>
      {typeof children === 'string' ? <Text color="gray.600">{children}</Text> : children}
    </VStack>
  </Box>
);

export default SeamlessPaymentsRefunds;
